/**
 * Debug Panel Store
 *
 * Manages the debug panel UI state (open/closed, active tab).
 * Persisted to localStorage so it remembers across page refreshes.
 * Only used in development mode.
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import { persist, createJSONStorage } from 'zustand/middleware'

/** Dashboard layout tier — matches useDashboardTier hook output */
export type DashboardTier = 'newcomer' | 'explorer' | 'veteran'

export type DebugTab = 'state' | 'flags' | 'network' | 'routes' | 'auth' | 'theme'

interface DebugPanelState {
  isOpen: boolean
  activeTab: DebugTab
  /** Override dashboard tier for internal review demos (null = use computed) */
  dashboardTierOverride: DashboardTier | null
}

interface DebugPanelActions {
  toggle: () => void
  open: () => void
  close: () => void
  setActiveTab: (tab: DebugTab) => void
  setDashboardTierOverride: (tier: DashboardTier | null) => void
}

type DebugPanelStore = DebugPanelState & DebugPanelActions

export const useDebugPanelStore = createWithEqualityFn<DebugPanelStore>()(
  persist(
    (set) => ({
      isOpen: false,
      activeTab: 'auth',
      dashboardTierOverride: null,
      toggle: () => set((s) => ({ isOpen: !s.isOpen })),
      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      setActiveTab: (tab) => set({ activeTab: tab }),
      setDashboardTierOverride: (tier) => set({ dashboardTierOverride: tier }),
    }),
    {
      name: 'docurious-debug-panel',
      storage: createJSONStorage(() => localStorage),
    },
  ),
  shallow,
)
